import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PauseIcon from '@mui/icons-material/Pause';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import SkipNextIcon from '@mui/icons-material/SkipNext';
import SkipPreviousIcon from '@mui/icons-material/SkipPrevious';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { observer } from 'mobx-react-lite';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { getContentStyle } from './getContentStyle';

/* ─── Design tokens ──────────────────────────────────────────────────────── */
const C = {
  bg:        '#0e0e11',
  surface:   '#16161a',
  surfaceHi: '#1e1e24',
  border:    'rgba(255,255,255,0.07)',
  borderHi:  'rgba(255,255,255,0.14)',
  pink:      '#e91e8c',
  pinkDim:   'rgba(233,30,140,0.15)',
  pinkGlow:  'rgba(233,30,140,0.35)',
  white:     '#f2f2f5',
  muted:     'rgba(242,242,245,0.45)',
  faint:     'rgba(242,242,245,0.2)',
  green:     '#22c55e',
  greenDim:  'rgba(34,197,94,0.15)',
  red:       '#ef4444',
  redDim:    'rgba(239,68,68,0.15)',
  blue:      '#3b82f6',
  blueDim:   'rgba(59,130,246,0.15)',
};

const FONT = "'Outfit', 'Inter', system-ui, -apple-system, sans-serif";
const MONO = "'SF Mono', 'Fira Code', 'Fira Mono', 'Roboto Mono', monospace";
const BASKET_W = 330;

const SENTIMENT = {
  positive: { bg: C.greenDim, color: C.green },
  negative: { bg: C.redDim,   color: C.red   },
  neutral:  { bg: 'rgba(255,255,255,0.06)', color: C.muted },
};

const fmtTime = (sec) => {
  if (sec == null) return '—';
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
};

/* ─── Chip ───────────────────────────────────────────────────────────────── */
const Pill = ({ label, bg, color }) => (
  <Box component="span" sx={{
    display: 'inline-block',
    px: '6px', py: '1px',
    borderRadius: '20px',
    fontSize: '10px',
    fontFamily: FONT,
    fontWeight: 500,
    letterSpacing: '0.3px',
    textTransform: 'capitalize',
    backgroundColor: bg,
    color: color,
    lineHeight: '16px',
  }}>
    {label}
  </Box>
);

/* ─── Clip card ──────────────────────────────────────────────────────────── */
const ClipCard = ({ row, isSelected, selectionOrder, isActive, onToggle, onPreview }) => {
  const sent = (row.sentiment || 'neutral').toLowerCase();
  const sentStyle = SENTIMENT[sent] || SENTIMENT.neutral;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        px: '14px',
        py: '12px',
        mb: '6px',
        borderRadius: '10px',
        backgroundColor: isSelected ? C.pinkDim : C.surface,
        border: `1px solid ${isSelected ? C.pinkGlow : C.border}`,
        boxShadow: isActive ? `0 0 0 2px ${C.pink}` : 'none',
        transition: 'all 0.18s ease',
        cursor: 'default',
        '&:hover': {
          backgroundColor: isSelected ? C.pinkDim : C.surfaceHi,
          borderColor: isSelected ? C.pinkGlow : C.borderHi,
        },
      }}
    >
      {/* Select circle */}
      <Box
        onClick={() => onToggle(row)}
        sx={{ flexShrink: 0, cursor: 'pointer', lineHeight: 0, position: 'relative' }}
      >
        {isSelected ? (
          <CheckCircleIcon sx={{ color: C.pink, fontSize: 22, display: 'block' }} />
        ) : (
          <RadioButtonUncheckedIcon sx={{ color: C.faint, fontSize: 22, display: 'block', '&:hover': { color: C.muted } }} />
        )}
        {isSelected && selectionOrder != null && (
          <Box sx={{
            position: 'absolute', bottom: -4, right: -4,
            width: 14, height: 14, borderRadius: '50%',
            backgroundColor: C.pink,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            border: `1.5px solid ${C.bg}`,
          }}>
            <Typography sx={{ fontSize: '8px', color: '#fff', fontWeight: 700, lineHeight: 1 }}>
              {selectionOrder}
            </Typography>
          </Box>
        )}
      </Box>

      {/* Thumbnail */}
      <Box
        onClick={() => onPreview(row)}
        sx={{
          flexShrink: 0, width: 96, height: 64,
          borderRadius: '7px', overflow: 'hidden',
          position: 'relative', cursor: 'pointer',
          backgroundColor: '#000',
          '&:hover .play-hint': { opacity: 1 },
        }}
      >
        {row.thumbnail && (
          <img src={row.thumbnail} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
        )}
        <Box className="play-hint" sx={{
          position: 'absolute', inset: 0,
          backgroundColor: 'rgba(0,0,0,0.45)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          opacity: 0, transition: 'opacity 0.15s',
        }}>
          <Box sx={{ width: 28, height: 28, borderRadius: '50%', backgroundColor: 'rgba(255,255,255,0.9)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <PlayArrowIcon sx={{ fontSize: 16, color: '#111', ml: '2px' }} />
          </Box>
        </Box>
      </Box>

      {/* Content */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{
          fontFamily: FONT, fontSize: '13px', fontWeight: 500,
          color: C.white, lineHeight: 1.45, mb: '5px',
          display: '-webkit-box', WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical', overflow: 'hidden',
        }}>
          {row.sentence}
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '5px', alignItems: 'center' }}>
          {row.start_in != null && (
            <Typography sx={{ fontFamily: MONO, fontSize: '10px', color: C.muted }}>
              {fmtTime(row.start_in)} → {fmtTime(row.end_in)}
            </Typography>
          )}
          {row.sentiment && <Pill label={row.sentiment} bg={sentStyle.bg} color={sentStyle.color} />}
          {row.question && <Pill label="?" bg={C.blueDim} color={C.blue} />}
        </Box>
      </Box>

      {/* Stats */}
      <Box sx={{ flexShrink: 0, textAlign: 'right', display: 'flex', flexDirection: 'column', gap: '3px', alignItems: 'flex-end' }}>
        {row.sec_int != null && (
          <Typography sx={{ fontFamily: MONO, fontSize: '11px', color: C.muted }}>{row.sec_int}s</Typography>
        )}
        {row.length != null && (
          <Typography sx={{ fontFamily: MONO, fontSize: '10px', color: C.faint }}>{row.length}c</Typography>
        )}
      </Box>
    </Box>
  );
};

/* ─── Custom video progress bar ──────────────────────────────────────────── */
const ProgressBar = ({ videoRef, activeClip }) => {
  const [progress, setProgress] = useState(0);
  const barRef = useRef(null);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const update = () => {
      if (!activeClip) return;
      const dur = (activeClip.end_in ?? 0) - (activeClip.start_in ?? 0);
      const pos = video.currentTime - (activeClip.start_in ?? 0);
      setProgress(dur > 0 ? Math.min(Math.max(pos / dur, 0), 1) : 0);
    };
    video.addEventListener('timeupdate', update);
    return () => video.removeEventListener('timeupdate', update);
  }, [videoRef, activeClip]);

  const handleClick = (e) => {
    const video = videoRef.current;
    if (!video || !activeClip || !barRef.current) return;
    const rect = barRef.current.getBoundingClientRect();
    const pct = (e.clientX - rect.left) / rect.width;
    const dur = (activeClip.end_in ?? 0) - (activeClip.start_in ?? 0);
    video.currentTime = (activeClip.start_in ?? 0) + pct * dur;
  };

  return (
    <Box ref={barRef} onClick={handleClick} sx={{
      height: '3px', borderRadius: '2px',
      backgroundColor: 'rgba(255,255,255,0.12)',
      cursor: 'pointer', overflow: 'hidden',
      '&:hover': { height: '5px', mt: '-1px' },
      transition: 'height 0.12s ease',
    }}>
      <Box sx={{ width: `${progress * 100}%`, height: '100%', backgroundColor: C.pink, borderRadius: '2px', transition: 'width 0.1s linear' }} />
    </Box>
  );
};

/* ─── Basket panel ───────────────────────────────────────────────────────── */
const BasketPanel = ({ selectedRows, videoFile, activeClip, onClipClick }) => {
  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);

  // Refs to avoid stale closures in the single timeupdate handler
  const activeClipRef = useRef(activeClip);
  const selectedRowsRef = useRef(selectedRows);
  const onClipClickRef = useRef(onClipClick);

  useEffect(() => { activeClipRef.current = activeClip; }, [activeClip]);
  useEffect(() => { selectedRowsRef.current = selectedRows; }, [selectedRows]);
  useEffect(() => { onClipClickRef.current = onClipClick; }, [onClipClick]);

  // Register timeupdate once — auto-advances to next clip
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const handleTimeUpdate = () => {
      const clip = activeClipRef.current;
      if (!clip || clip.end_in == null) return;
      if (video.currentTime >= clip.end_in - 0.08) {
        const rows = selectedRowsRef.current;
        const idx = rows.findIndex(r => r.id === clip.id);
        const next = rows[idx + 1];
        if (next) {
          onClipClickRef.current(next);
        } else {
          video.pause();
          setIsPlaying(false);
        }
      }
    };
    video.addEventListener('timeupdate', handleTimeUpdate);
    return () => video.removeEventListener('timeupdate', handleTimeUpdate);
  }, []); // empty — uses refs

  // Seek + play when activeClip changes
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !activeClip) return;
    video.currentTime = activeClip.start_in ?? 0;
    video.play().then(() => setIsPlaying(true)).catch(() => {});
  }, [activeClip]);

  const togglePlay = () => {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) { video.play().then(() => setIsPlaying(true)).catch(() => {}); }
    else { video.pause(); setIsPlaying(false); }
  };

  const skipTo = useCallback((dir) => {
    if (!activeClip) {
      if (selectedRows.length > 0) onClipClick(selectedRows[0]);
      return;
    }
    const idx = selectedRows.findIndex(r => r.id === activeClip.id);
    const target = selectedRows[idx + dir];
    if (target) onClipClick(target);
  }, [activeClip, selectedRows, onClipClick]);

  const currentIdx = activeClip ? selectedRows.findIndex(r => r.id === activeClip.id) : -1;

  return (
    <Box sx={{
      width: BASKET_W, flexShrink: 0,
      display: 'flex', flexDirection: 'column',
      borderLeft: `1px solid ${C.border}`,
      height: '100%',
      backgroundColor: C.bg,
    }}>
      {/* ── Header ── */}
      <Box sx={{
        px: '18px', py: '13px',
        borderBottom: `1px solid ${C.border}`,
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      }}>
        <Typography sx={{ fontFamily: FONT, fontWeight: 700, fontSize: '13px', color: C.white, letterSpacing: '0.2px' }}>
          Editing Basket
        </Typography>
        {selectedRows.length > 0 && (
          <Box sx={{ px: '8px', py: '2px', borderRadius: '20px', backgroundColor: C.pinkDim, border: `1px solid ${C.pinkGlow}` }}>
            <Typography sx={{ fontFamily: FONT, fontSize: '11px', fontWeight: 600, color: C.pink }}>
              {selectedRows.length} {selectedRows.length === 1 ? 'clip' : 'clips'}
            </Typography>
          </Box>
        )}
      </Box>

      {/* ── Video player ── */}
      <Box sx={{ position: 'relative', backgroundColor: '#000', aspectRatio: '16/9', flexShrink: 0 }}>
        <video
          ref={videoRef}
          src={videoFile}
          style={{ width: '100%', height: '100%', display: 'block', objectFit: 'contain' }}
          preload="metadata"
        />
        {!activeClip && (
          <Box sx={{
            position: 'absolute', inset: 0,
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '10px',
          }}>
            <Box sx={{ width: 44, height: 44, borderRadius: '50%', backgroundColor: 'rgba(255,255,255,0.07)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <PlayArrowIcon sx={{ color: 'rgba(255,255,255,0.3)', fontSize: 26, ml: '3px' }} />
            </Box>
            <Typography sx={{ fontFamily: FONT, color: 'rgba(255,255,255,0.3)', fontSize: '11px' }}>
              Click a clip or select &amp; Play All
            </Typography>
          </Box>
        )}
      </Box>

      {/* ── Playback controls ── */}
      <Box sx={{ px: '14px', py: '10px', borderBottom: `1px solid ${C.border}`, backgroundColor: C.surface }}>
        {/* Progress */}
        <Box sx={{ mb: '8px' }}>
          <ProgressBar videoRef={videoRef} activeClip={activeClip} />
        </Box>
        {/* Controls row */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <Tooltip title="Previous clip" placement="top">
            <span>
              <IconButton onClick={() => skipTo(-1)} disabled={currentIdx <= 0} size="small"
                sx={{ color: currentIdx > 0 ? C.muted : C.faint, p: '4px', '&:hover': { color: C.white }, '&.Mui-disabled': { color: C.faint } }}>
                <SkipPreviousIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </span>
          </Tooltip>
          <IconButton onClick={togglePlay} size="small" sx={{
            width: 30, height: 30, borderRadius: '50%',
            backgroundColor: activeClip ? C.pink : 'rgba(255,255,255,0.08)',
            color: '#fff', p: 0,
            '&:hover': { backgroundColor: activeClip ? '#c41874' : 'rgba(255,255,255,0.14)' },
          }}>
            {isPlaying ? <PauseIcon sx={{ fontSize: 16 }} /> : <PlayArrowIcon sx={{ fontSize: 16, ml: '1px' }} />}
          </IconButton>
          <Tooltip title="Next clip" placement="top">
            <span>
              <IconButton onClick={() => skipTo(1)} disabled={currentIdx >= selectedRows.length - 1 || currentIdx < 0} size="small"
                sx={{ color: (currentIdx >= 0 && currentIdx < selectedRows.length - 1) ? C.muted : C.faint, p: '4px', '&:hover': { color: C.white }, '&.Mui-disabled': { color: C.faint } }}>
                <SkipNextIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </span>
          </Tooltip>
          <Box sx={{ flex: 1 }} />
          {activeClip && (
            <Typography sx={{ fontFamily: MONO, fontSize: '10px', color: C.muted }}>
              {fmtTime(activeClip.start_in)} – {fmtTime(activeClip.end_in)}
            </Typography>
          )}
          {selectedRows.length > 0 && currentIdx >= 0 && (
            <Typography sx={{ fontFamily: FONT, fontSize: '10px', color: C.faint, ml: '8px' }}>
              {currentIdx + 1} / {selectedRows.length}
            </Typography>
          )}
        </Box>
      </Box>

      {/* ── Clips grid ── */}
      <Box sx={{ px: '14px', pt: '12px', pb: '6px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography sx={{ fontFamily: FONT, fontSize: '10px', fontWeight: 600, color: C.faint, textTransform: 'uppercase', letterSpacing: '1px' }}>
          Clips in order
        </Typography>
      </Box>

      <Box sx={{ flex: 1, overflowY: 'auto', px: '10px', pb: '10px',
        '&::-webkit-scrollbar': { width: '4px' },
        '&::-webkit-scrollbar-track': { background: 'transparent' },
        '&::-webkit-scrollbar-thumb': { background: 'rgba(255,255,255,0.1)', borderRadius: '2px' },
      }}>
        {selectedRows.length === 0 ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 100, gap: '8px' }}>
            <Typography sx={{ fontFamily: FONT, fontSize: '12px', color: C.faint, textAlign: 'center', lineHeight: 1.6 }}>
              Select clips to<br />build your edit
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '6px' }}>
            {selectedRows.map((row, idx) => {
              const isActiveThumb = activeClip?.id === row.id;
              return (
                <Tooltip key={row.id ?? idx} title={row.sentence || ''} placement="top" arrow>
                  <Box
                    onClick={() => onClipClick(row)}
                    sx={{
                      position: 'relative', borderRadius: '6px', overflow: 'hidden',
                      cursor: 'pointer', aspectRatio: '16/9',
                      border: `2px solid ${isActiveThumb ? C.pink : 'transparent'}`,
                      boxShadow: isActiveThumb ? `0 0 8px ${C.pinkGlow}` : 'none',
                      transition: 'all 0.15s ease',
                      '&:hover': { transform: 'scale(1.04)', borderColor: isActiveThumb ? C.pink : 'rgba(255,255,255,0.25)' },
                    }}
                  >
                    {row.thumbnail && (
                      <img src={row.thumbnail} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                    )}
                    {/* Number badge */}
                    <Box sx={{
                      position: 'absolute', top: '3px', left: '3px',
                      minWidth: 16, height: 16, px: '3px',
                      borderRadius: '8px', backgroundColor: isActiveThumb ? C.pink : 'rgba(0,0,0,0.72)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      transition: 'background-color 0.15s',
                    }}>
                      <Typography sx={{ fontFamily: FONT, fontSize: '8px', color: '#fff', fontWeight: 700, lineHeight: 1 }}>{idx + 1}</Typography>
                    </Box>
                    {/* Playing indicator */}
                    {isActiveThumb && (
                      <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(0,0,0,0.2)' }}>
                        <PlayArrowIcon sx={{ color: 'rgba(255,255,255,0.85)', fontSize: 16 }} />
                      </Box>
                    )}
                  </Box>
                </Tooltip>
              );
            })}
          </Box>
        )}
      </Box>
    </Box>
  );
};

/* ─── Main ───────────────────────────────────────────────────────────────── */
const CherryPickGrid = observer(({ toolbarPosition, data, videoFile, bottomOffset, currentView, filterStore, onCheckboxChange }) => {
  const [activeClip, setActiveClip] = useState(null);

  const selectedIdsList = filterStore.getSelectedSentences(currentView);
  const selectedIds = new Set(selectedIdsList);

  const selectedRows = data
    .filter(r => selectedIds.has(r.id))
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

  const handleToggle = (row) => {
    const checked = !selectedIds.has(row.id);
    const idx = data.findIndex(r => r.id === row.id);
    if (onCheckboxChange) onCheckboxChange({ target: { checked } }, idx, row.id);
  };

  const playAll = () => {
    if (selectedRows.length > 0) setActiveClip(selectedRows[0]);
  };

  return (
    <Box sx={{
      ...getContentStyle(toolbarPosition),
      display: 'flex',
      width: '100%',
      height: '100%',
      overflow: 'hidden',
      backgroundColor: C.bg,
    }}>
      {/* ── Left: clip list ── */}
      <Box sx={{
        flex: 1, overflowY: 'auto',
        px: '16px', py: '14px',
        '&::-webkit-scrollbar': { width: '4px' },
        '&::-webkit-scrollbar-track': { background: 'transparent' },
        '&::-webkit-scrollbar-thumb': { background: 'rgba(255,255,255,0.1)', borderRadius: '2px' },
      }}>
        {/* Stats + Play All */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: '14px', px: '2px' }}>
          <Typography sx={{ fontFamily: FONT, fontSize: '12px', color: C.muted }}>
            {data.length} clips
            {selectedIds.size > 0 && (
              <> · <span style={{ color: C.pink, fontWeight: 600 }}>{selectedIds.size} selected</span></>
            )}
          </Typography>
          {selectedRows.length > 0 && (
            <Box
              onClick={playAll}
              sx={{
                display: 'flex', alignItems: 'center', gap: '5px',
                px: '12px', py: '4px',
                borderRadius: '20px',
                backgroundColor: C.pink,
                cursor: 'pointer',
                transition: 'background-color 0.15s',
                '&:hover': { backgroundColor: '#c41874' },
              }}
            >
              <PlayArrowIcon sx={{ fontSize: 13, color: '#fff' }} />
              <Typography sx={{ fontFamily: FONT, fontSize: '11px', fontWeight: 600, color: '#fff' }}>
                Play All
              </Typography>
            </Box>
          )}
        </Box>

        {/* Clip cards */}
        {data.map((row, index) => (
          <ClipCard
            key={row.id ?? index}
            row={row}
            isSelected={selectedIds.has(row.id)}
            selectionOrder={selectedIds.has(row.id) ? row.order : null}
            isActive={activeClip?.id === row.id}
            onToggle={handleToggle}
            onPreview={setActiveClip}
          />
        ))}
      </Box>

      {/* ── Right: basket ── */}
      <BasketPanel
        selectedRows={selectedRows}
        videoFile={videoFile}
        activeClip={activeClip}
        onClipClick={setActiveClip}
      />
    </Box>
  );
});

export default CherryPickGrid;
